import React, { useState } from "react";
import { Modal, Table } from "antd";
import { LuX, LuPlay } from "react-icons/lu";
import AssignInterpreterModal from "./AssignInterpreterModal";
import ImageDummy from "../../assets/images/Image (1).png";
import {
  FileVideo,
  Frown,
  Meh,
  SmilePlus,
  Smile,
  SmileIcon,
  Star,
} from "lucide-react";
import { selectCurrentRole } from "../../store/auth/authSlice";
import { useSelector } from "react-redux";
import { getFileKindFromUrl } from "../../utils/filePreview";
import FileResponsePreview from "../common/FileResponsePreview";
import {
  getRadioListDisplayLines,
  extractRadioListConfig,
  getRadioListTypeLabel,
} from "../../utils/radioList";
import {
  hasExplicitSliderSelection,
  isAnonymousExportInput,
  isAnonymousExportResponse,
} from "../../utils/reportsHelper";

const InputResponsesModal = ({
  visible,
  onClose,
  inputType,
  inputData,
  organizationId,
  questionId,
}) => {
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const userRole = useSelector(selectCurrentRole);

  // Helper function to get emoji based on index and scale
  const getEmoji = (index, totalOptions = 5) => {
    // Define emoji sets based on scale
    const emojiSets = {
      4: [
        { Icon: Frown, color: "#FF4D4F" },
        { Icon: Meh, color: "#FFA940" },
        { Icon: Smile, color: "#52C41A" },
        { Icon: SmilePlus, color: "#1890FF" },
      ],
      5: [
        { Icon: Frown, color: "#FF4D4F" },
        { Icon: Meh, color: "#FFA940" },
        { Icon: SmileIcon, color: "#FADB14" },
        { Icon: Smile, color: "#52C41A" },
        { Icon: SmilePlus, color: "#1890FF" },
      ],
      7: [
        { Icon: Frown, color: "#FF4D4F" },
        { Icon: Frown, color: "#FF7A45" },
        { Icon: Meh, color: "#FFA940" },
        { Icon: SmileIcon, color: "#FADB14" },
        { Icon: Smile, color: "#73D13D" },
        { Icon: Smile, color: "#52C41A" },
        { Icon: SmilePlus, color: "#1890FF" },
      ],
    };

    // Get the emoji set for the given scale, default to 5
    const emojiSet = emojiSets[totalOptions.toString()] || emojiSets["5"];

    // Get the emoji component and color
    const emojiData = emojiSet[index] || emojiSet[0];

    // Return the emoji component with its color
    return {
      Icon: emojiData.Icon,
      color: emojiData.color,
    };
  };

  // Helper function to get emoji descriptions by index
  const getEmojiDescriptionByIndex = (index, totalOptions = 5) => {
    // First check if we have input options directly in the inputData
    if (
      inputData &&
      inputData.options &&
      Array.isArray(inputData.options) &&
      inputData.options.length > 0
    ) {
      // Make sure the index is in bounds
      if (index >= 0 && index < inputData.options.length) {
        return inputData.options[index];
      } else if (inputData.options.length > 0) {
        // If index is out of bounds but we have options, use the closest one
        const clampedIndex = Math.min(
          Math.max(0, index),
          inputData.options.length - 1
        );
        return inputData.options[clampedIndex];
      }
    }

    // If we have input_options in the input field data, use those
    if (
      inputData &&
      inputData.input_options &&
      Array.isArray(inputData.input_options) &&
      inputData.input_options.length > 0
    ) {
      // Make sure the index is in bounds
      if (index >= 0 && index < inputData.input_options.length) {
        return inputData.input_options[index];
      } else if (inputData.input_options.length > 0) {
        // If index is out of bounds but we have options, use the closest one
        const clampedIndex = Math.min(
          Math.max(0, index),
          inputData.input_options.length - 1
        );
        return inputData.input_options[clampedIndex];
      }
    }

    // If we have scale_options in the input field data, use those
    if (
      inputData &&
      inputData.scale_options &&
      Array.isArray(inputData.scale_options) &&
      inputData.scale_options.length > 0
    ) {
      // Make sure the index is in bounds
      if (index >= 0 && index < inputData.scale_options.length) {
        return inputData.scale_options[index];
      } else if (inputData.scale_options.length > 0) {
        // If index is out of bounds but we have options, use the closest one
        const clampedIndex = Math.min(
          Math.max(0, index),
          inputData.scale_options.length - 1
        );
        return inputData.scale_options[clampedIndex];
      }
    }

    // Fall back to these predefined descriptions if no options are found in the data

    // For 5-option scale (most common)
    if (totalOptions === 5) {
      const descriptions = [
        "Very Unsatisfied",
        "Unsatisfied",
        "Neutral",
        "Satisfied",
        "Very Satisfied",
      ];
      return descriptions[index] || `Rating ${index + 1}`;
    }

    // For 4-option scale
    else if (totalOptions === 4) {
      const descriptions = ["Poor", "Fair", "Good", "Excellent"];
      return descriptions[index] || `Rating ${index + 1}`;
    }

    // For 7-option scale
    else if (totalOptions === 7) {
      const descriptions = [
        "Extremely Poor",
        "Very Poor",
        "Poor",
        "Neutral",
        "Good",
        "Very Good",
        "Excellent",
      ];
      return descriptions[index] || `Rating ${index + 1}`;
    }

    // Default fallback
    return `Rating ${index + 1} of ${totalOptions}`;
  };

  // Function to determine emoji index from slider value
  const getEmojiIndexFromValue = (value, totalOptions = 5) => {
    if (typeof value !== "number" || isNaN(value)) return null;

    // Convert the value (0-100) to an index
    const normalizedIndex = Math.round((value / 100) * (totalOptions - 1));
    return Math.max(0, Math.min(normalizedIndex, totalOptions - 1));
  };

  const getSliderNumericDisplay = (rawValue, emojiIndex, totalOptions) => {
    const parsedValue =
      typeof rawValue === "number"
        ? rawValue
        : typeof rawValue === "string" && rawValue.trim() !== ""
          ? Number(rawValue)
          : null;

    if (typeof parsedValue === "number" && !Number.isNaN(parsedValue)) {
      const rounded = Math.round(parsedValue);

      // 0-based index persisted as numeric (0..N-1)
      if (
        Number.isInteger(rounded) &&
        rounded >= 0 &&
        rounded < totalOptions &&
        totalOptions > 1
      ) {
        return `${rounded + 1}/${totalOptions}`;
      }

      // 1-based point scale persisted as numeric (1..N)
      if (
        Number.isInteger(rounded) &&
        rounded >= 1 &&
        rounded <= totalOptions &&
        totalOptions > 1
      ) {
        return `${rounded}/${totalOptions}`;
      }

      // True percentage slider value
      if (rounded >= 0 && rounded <= 100) {
        return `${rounded}/100`;
      }
    }

    // Fallback to derived option index
    return `${emojiIndex + 1}/${totalOptions}`;
  };

  const getFileType = (url) =>
    getFileKindFromUrl(url, inputData?.file_type || inputData?.fileType);

  const getExtraInfo = () => {
    switch (inputType) {
      case "file":
      case "multiple-files":
        return {
          label: "File Type",
          value: "JPG, PNG, JPEG",
        };
      case "date":
        return {
          label: "Date Type",
          value: inputData?.dateType || "Date Range",
        };
      case "record-video":
        return {
          label: "Recording Limit",
          value: inputData?.inputType?.includes("- 30 second limit")
            ? "30 seconds"
            : inputData?.inputType?.includes("- 60 second limit")
            ? "60 seconds"
            : "No limit",
        };
      case "dropdown":
      case "checkbox":
      case "radio":
        return {
          label: `${
            inputType === "dropdown"
              ? "Drop Down"
              : inputType === "checkbox"
              ? "Checkbox"
              : "Radio"
          } Options`,
          value: Array.isArray(inputData?.options)
            ? inputData.options.join(", ")
            : "No options available",
        };
      case "checkbox-list":
      case "radio-list": {
        const { headers, rows } = extractRadioListConfig(inputData);
        return {
          label: getRadioListTypeLabel(inputType),
          value: [
            `Headers: ${headers.join(", ") || "None"}`,
            `Rows: ${rows.join(", ") || "None"}`,
          ].join(" | "),
        };
      }
      case "slider":
        // Extract slider options from inputData - try all possible paths
        let sliderOptions = [];

        if (
          inputData?.options &&
          Array.isArray(inputData.options) &&
          inputData.options.length > 0
        ) {
          sliderOptions = inputData.options;
        } else if (
          inputData?.input_options &&
          Array.isArray(inputData.input_options) &&
          inputData.input_options.length > 0
        ) {
          sliderOptions = inputData.input_options;
        } else if (
          inputData?.scale_options &&
          Array.isArray(inputData.scale_options) &&
          inputData.scale_options.length > 0
        ) {
          sliderOptions = inputData.scale_options;
        } else {
          // Get default options based on scale type
          const scaleType = inputData?.scale_type || 5;
          if (scaleType === 4) {
            sliderOptions = ["Poor", "Fair", "Good", "Excellent"];
          } else if (scaleType === 7) {
            sliderOptions = [
              "Extremely Poor",
              "Very Poor",
              "Poor",
              "Neutral",
              "Good",
              "Very Good",
              "Excellent",
            ];
          } else {
            sliderOptions = [
              "Very Unsatisfied",
              "Unsatisfied",
              "Neutral",
              "Satisfied",
              "Very Satisfied",
            ];
          }
        }

        return {
          label: "Slider Options (Low to High)",
          value: sliderOptions.join(", "),
        };
      default:
        return null;
    }
  };

  const handlePreview = (url) => {
    setPreviewUrl(url);
    setPreviewVisible(true);
  };

  // Get translation for a specific response record
  const getTranslation = (record) => {
    if (!record || !record.id) return null;
    try {
      // Get translation from response_assignments directly from the record
      if (record.response_assignments) {
        const responseAssignment = record.response_assignments.find(
          (assignment) => assignment.question === questionId
        );

        if (
          responseAssignment?.translation &&
          responseAssignment.translation.length > 0
        ) {
          return responseAssignment.translation[0].text;
        }
      }

      return null;
    } catch (error) {
      console.warn("Error getting translation:", error);
      return null;
    }
  };

  const getResponseColumns = () => {
    const baseColumns = [
      {
        title: "Sr#",
        dataIndex: "srNo",
        key: "srNo",
        render: (_, __, index) => index + 1,
        width: 70,
      },

      {
        title: "Participant",
        dataIndex: "participant",
        key: "participant",
        render: (_, record) => (
          <div className="flex items-center gap-3">
            <img
              src={ImageDummy}
              alt={record?.name || "User"}
              className="w-10 h-10 rounded-full"
            />
            <div>
              <div className="font-medium">{record.name || "—"}</div>
              <div className="text-sm text-gray-500">
                {record.email || "—"}
              </div>
            </div>
          </div>
        ),
      },
    ];

    const responseColumn = {
      title: "Response",
      dataIndex: "response",
      key: "response",
      render: (response, record) => {
        // Handle different types of response formats
        const responseValue = Array.isArray(response)
          ? response
          : response && typeof response === "string"
          ? response
          : record?.response;

        switch (inputType) {
          // Updated slider case in getResponseColumns function
          case "slider":
            if (
              !hasExplicitSliderSelection(responseValue) &&
              !(
                record?.name &&
                typeof record.name === "object" &&
                hasExplicitSliderSelection(record.name)
              )
            ) {
              return <div className="text-gray-500">No response</div>;
            }

            // Get the slider options directly from the same source used in getExtraInfo
            let sliderOptions = [];

            if (
              inputData?.options &&
              Array.isArray(inputData.options) &&
              inputData.options.length > 0
            ) {
              sliderOptions = inputData.options;
            } else if (
              inputData?.input_options &&
              Array.isArray(inputData.input_options) &&
              inputData.input_options.length > 0
            ) {
              sliderOptions = inputData.input_options;
            } else if (
              inputData?.scale_options &&
              Array.isArray(inputData.scale_options) &&
              inputData.scale_options.length > 0
            ) {
              sliderOptions = inputData.scale_options;
            } else {
              // Get default options based on scale type
              const scaleType = inputData?.scale_type || 5;
              if (scaleType === 4) {
                sliderOptions = ["Poor", "Fair", "Good", "Excellent"];
              } else if (scaleType === 7) {
                sliderOptions = [
                  "Extremely Poor",
                  "Very Poor",
                  "Poor",
                  "Neutral",
                  "Good",
                  "Very Good",
                  "Excellent",
                ];
              } else {
                sliderOptions = [
                  "Very Unsatisfied",
                  "Unsatisfied",
                  "Neutral",
                  "Satisfied",
                  "Very Satisfied",
                ];
              }
            }

            const totalOptions = sliderOptions.length;

            // Extract slider values and determine emoji index
            let sliderValue, emojiIndex;

            // Check if response is an object with the values we need
            if (
              responseValue &&
              typeof responseValue === "object" &&
              !Array.isArray(responseValue)
            ) {
              sliderValue = responseValue.value;
              if (responseValue.emojiIndex !== undefined) {
                emojiIndex = responseValue.emojiIndex;
              } else if (sliderValue !== undefined) {
                // Calculate index from value
                emojiIndex = getEmojiIndexFromValue(sliderValue, totalOptions);
              }
            }
            // Check if name contains the values
            else if (
              record.name &&
              typeof record.name === "object" &&
              !Array.isArray(record.name)
            ) {
              sliderValue = record.name.value;
              if (record.name.emojiIndex !== undefined) {
                emojiIndex = record.name.emojiIndex;
              } else if (sliderValue !== undefined) {
                // Calculate index from value
                emojiIndex = getEmojiIndexFromValue(sliderValue, totalOptions);
              }
            }
            // Handle if it's just a direct number
            else if (
              responseValue !== undefined &&
              !isNaN(parseFloat(responseValue))
            ) {
              sliderValue = parseFloat(responseValue);
              // Calculate emojiIndex based on the value
              emojiIndex = getEmojiIndexFromValue(sliderValue, totalOptions);
            }
            // Fall back to checking the name as a direct number
            else if (
              record.name !== undefined &&
              !isNaN(parseFloat(record.name))
            ) {
              sliderValue = parseFloat(record.name);
              emojiIndex = getEmojiIndexFromValue(sliderValue, totalOptions);
            } else {
              return <div className="text-gray-500">No response</div>;
            }

            if (emojiIndex == null || Number.isNaN(Number(emojiIndex))) {
              return <div className="text-gray-500">No response</div>;
            }

            // Ensure emojiIndex is within bounds
            emojiIndex = Math.max(0, Math.min(emojiIndex, totalOptions - 1));

            // Get the description directly from the slider options
            const description =
              sliderOptions[emojiIndex] || `Rating ${emojiIndex + 1}`;

            // Check if stars should be shown
            const showStars = inputData?.show_stars || inputData?.slider_config?.show_stars || false;

            // Calculate numeric value
            const numericDisplay = getSliderNumericDisplay(
              sliderValue,
              emojiIndex,
              totalOptions,
            );

            // Format the display value with stars or emoji
            if (showStars) {
              // Show stars - all stars up to selected index are filled
              const yellowColor = "#FFD700";
              return (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: totalOptions }, (_, idx) => (
                        <Star
                          key={idx}
                          size={20}
                          color={yellowColor}
                          fill={idx <= emojiIndex ? yellowColor : "none"}
                          strokeWidth={2}
                        />
                      ))}
                    </div>
                    <span>{description}</span>
                  </div>
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                    {numericDisplay}
                  </span>
                </div>
              );
            } else {
              // Show emoji (existing behavior)
              const { Icon, color } = getEmoji(emojiIndex, totalOptions);
              return (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center">
                    <div className="mr-2">
                      <Icon size={20} color={color} />
                    </div>
                    <span>{description}</span>
                  </div>
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 rounded-full text-xs font-medium">
                    {numericDisplay}
                  </span>
                </div>
              );
            }

          case "name":
          case "text":
          case "paragraph":
          case "email": {
            const fieldAnswer =
              typeof responseValue === "string" ||
              typeof responseValue === "number"
                ? String(responseValue).trim()
                : "";

            if (inputType === "name" || inputType === "email") {
              return (
                <div className="text-gray-700">
                  {fieldAnswer || "No response"}
                </div>
              );
            }

            return (
              <div className="text-gray-700">
                {fieldAnswer || responseValue || "No response"}
              </div>
            );
          }

          case "number":
            return <div className="font-medium">{responseValue || "0"}</div>;
          case "date":
            return <div>{responseValue || record?.date || "No date"}</div>;

          case "record-video":
            // For video responses
            const videoUrl =
              typeof responseValue === "string" ? responseValue : null;
            if (!videoUrl)
              return <div className="text-gray-500">No video recorded</div>;

            // Find the translation for this record
            const translation = getTranslation(record);

            // Show video only to admin users, show just translation for other users
            if (userRole === "admin") {
              return (
                <div
                  className="relative w-48 h-24 rounded-lg overflow-hidden bg-gray-100 cursor-pointer"
                  onClick={() => handlePreview(videoUrl)}
                >
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <FileVideo className="text-gray-500" size={24} />
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
                      <LuPlay className="text-white" size={20} />
                    </div>
                  </div>
                  {translation && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-1 text-xs truncate">
                      {translation}
                    </div>
                  )}
                </div>
              );
            } else {
              // For non-admin users, only show the translation (if available)
              return (
                <div className="text-gray-700">
                  {/* {translation ? translation : "No translation available"} */}
                </div>
              );
            }

          case "file":
          case "multiple-files":
            return (
              <FileResponsePreview
                value={responseValue}
                fileTypeHint={inputData?.file_type || inputData?.fileType}
              />
            );

          case "checkbox":
            // For checkbox responses, handle both string and array formats
            let checkboxValues = [];
            if (Array.isArray(responseValue)) {
              checkboxValues = responseValue;
            } else if (typeof responseValue === "string") {
              checkboxValues = [responseValue];
            }

            if (checkboxValues.length === 0 && record?.name) {
              // If response is empty but name contains the selection
              checkboxValues = Array.isArray(record.name)
                ? record.name
                : [record.name];
            }

            return (
              <div className="flex flex-wrap gap-2">
                {checkboxValues.length > 0 ? (
                  checkboxValues.map((item, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 rounded-full text-sm"
                    >
                      {item}
                    </span>
                  ))
                ) : (
                  <div className="text-gray-500">No options selected</div>
                )}
              </div>
            );

          case "radio":
          case "dropdown": {
            const radioAnswer =
              responseValue && typeof responseValue === "object"
                ? responseValue.selection ??
                  responseValue.value ??
                  responseValue.otherValue
                : responseValue;
            const radioText =
              radioAnswer == null ? "" : String(radioAnswer).trim();

            return (
              <div className="text-gray-700">
                {radioText || "No response"}
              </div>
            );
          }

          case "checkbox-list":
          case "radio-list": {
            const items = getRadioListDisplayLines(responseValue);

            return (
              <div className="flex flex-col items-start gap-1">
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-gray-100 rounded-full text-sm"
                    >
                      {item}
                    </span>
                  ))
                ) : (
                  <div className="text-gray-500">No options selected</div>
                )}
              </div>
            );
          }

          default:
            return <div>{responseValue || "No response"}</div>;
        }
      },
    };

    const columns = [...baseColumns, responseColumn];

    // Add translations column only for record-video type
    if (inputType === "record-video") {
      columns.push({
        title: "Translations",
        dataIndex: "translations",
        key: "translations",
        render: (_, record) => {
          const translation = getTranslation(record);
          return (
            <div className="text-sm">
              {translation || "No translation available"}
            </div>
          );
        },
      });
    }

    return columns;
  };

  // Format the response data for the table
  const formatResponseData = () => {
    if (!inputData?.responseData || !Array.isArray(inputData.responseData)) {
      return [];
    }

    if (
      isAnonymousExportInput({
        title: inputData.input_title || inputData.inputTitle || inputData.inputType,
        inputTitle: inputData.input_title || inputData.inputTitle,
        type: inputType,
      })
    ) {
      return [];
    }

    return inputData.responseData
      .filter(
        (item) =>
          !isAnonymousExportResponse(item.response) &&
          !isAnonymousExportInput(
            {
              title: inputData.input_title || inputData.inputTitle,
              inputTitle: inputData.input_title || inputData.inputTitle,
              type: inputType,
            },
            item.response,
          ),
      )
      .map((item, index) => ({
        key: item.id || `response-${index}`,
        ...item,
      }));
  };

  return (
    <>
      <Modal
        open={visible}
        onCancel={onClose}
        footer={null}
        title={
          <div className="flex justify-between items-center -top-4 relative flex-wrap">
            <h3>Input Responses</h3>
            {inputType === "record-video" && (
              <button
                onClick={() => {
                  onClose();
                  setAssignModalVisible(true);
                }}
                className="px-6 py-2 bg-[#18181B] text-white rounded-xl text-sm font-medium mr-8"
              >
                Assign Interpreter
              </button>
            )}
          </div>
        }
        width={800}
        closeIcon={
          <div className="bg-secondary h-8 mt-2 w-8 flex justify-center items-center rounded-full">
            <LuX className="text-red-400" size={20} />
          </div>
        }
        className="responses-modal"
      >
        <div className="flex flex-col border rounded-2xl p-4 mb-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-4">
            <div>
              <div className="text-gray-500 text-sm mb-1">Input Type</div>
              <div className="text-gray-900 capitalize">
                {inputType || inputData?.type || "N/A"}
              </div>
            </div>
            <div className="col-span-3">
              <div className="text-gray-500 text-sm mb-1">Input Title</div>
              <div className="text-gray-900">
                {inputData?.inputTitle || inputData?.inputType || "N/A"}
              </div>
            </div>
            {getExtraInfo() && (
              <div className="col-span-2">
                <div className="text-gray-500 text-sm mb-1">
                  {getExtraInfo().label}
                </div>
                <div className="text-gray-900">{getExtraInfo().value}</div>
              </div>
            )}
          </div>
        </div>

        <Table
          columns={getResponseColumns()}
          dataSource={formatResponseData()}
          pagination={false}
          rowKey={(record, index) => record?.id || `row-${index}`}
          className="responses-table"
          scroll={{ x: 800 }}
        />
      </Modal>

      {/* File Preview Modal */}
      <Modal
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        // width="auto"
        closeIcon={
          <div className="bg-secondary h-8 w-8 flex justify-center items-center rounded-full">
            <LuX className="text-red-400" size={20} />
          </div>
        }
      >
        {previewUrl && getFileType(previewUrl) === "image" ? (
          <img
            src={previewUrl}
            alt="Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 200px)",
            }}
          />
        ) : previewUrl && getFileType(previewUrl) === "video" ? (
          <video
            src={previewUrl}
            controls
            style={{
              maxWidth: "100%",
              maxHeight: "calc(100vh - 200px)",
            }}
          />
        ) : (
          <div className="flex items-center justify-center p-8">
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              Open file in new tab
            </a>
          </div>
        )}
      </Modal>

      <AssignInterpreterModal
        visible={assignModalVisible}
        questionId={questionId}
        organizationId={organizationId}
        onClose={() => {
          setAssignModalVisible(false);
          onClose(false);
        }}
      />
    </>
  );
};

export default InputResponsesModal;
